import React, { useState, useEffect } from 'react';
import TopNavigation from './TopNavigation';
import Sidebar from './Sidebar';
import './Layout.css';

const Layout = ({ 
  children, 
  user, 
  onLogout, 
  showSidebar = true,
  currentRoute = '/',
  fullWidth = false
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Check localStorage for user preference
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentRoute]);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleNavigate = (path) => {
    // Close mobile menu after navigation
    if (isMobile) {
      setMobileMenuOpen(false);
    }
    
    // Navigate to the path
    window.location.hash = path;
  };

  const layoutClasses = [
    'layout',
    showSidebar && 'has-sidebar',
    showSidebar && !sidebarCollapsed && 'sidebar-expanded',
    showSidebar && sidebarCollapsed && 'sidebar-collapsed',
    isMobile && 'mobile',
    fullWidth && 'full-width'
  ].filter(Boolean).join(' ');

  return (
    <div className={layoutClasses}>
      {/* Top Navigation */}
      <TopNavigation
        user={user}
        onLogout={onLogout}
        onToggleSidebar={handleToggleSidebar}
        sidebarCollapsed={sidebarCollapsed}
        showSidebarToggle={showSidebar}
      />

      {/* Sidebar */}
      {showSidebar && (
        <>
          <Sidebar
            collapsed={isMobile ? false : sidebarCollapsed}
            currentRoute={currentRoute}
            onNavigate={handleNavigate}
          />
          
          {/* Mobile overlay */}
          {isMobile && mobileMenuOpen && (
            <div 
              className="mobile-sidebar-overlay"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
        </>
      )}

      {/* Main Content */}
      <main className="main-content">
        <div className="content-container">
          {children}
        </div>
      </main>

      {/* Mobile sidebar state management */}
      <style jsx>{`
        .layout.mobile .sidebar {
          transform: ${mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)'};
        }
      `}</style>
    </div>
  );
};

export default Layout;
